import React, { useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import moment from "moment";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableCell from "@mui/material/TableCell";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Slide from "@mui/material/Slide";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NumberFormat from "react-number-format";
import ISXUtils from "services/Utils";

import Widget from "../../widget/Widget";
import CSVExporter from "services/CSVExporter";

const ANIMATION_DURATION = 500;

const StickyColumnTableCell = styled(TableCell)({
  position: "sticky",
  left: 0,
  background: "white",
});

const TagLabel = ({ tag, stacks }) => {
  const stack = stacks?.[tag.stack];

  const dev_session = stack?.dev_session;
  const stackTitle = useMemo(() => {
    const title =
      (stack?.name || JSON.parse(dev_session || "{}")["stack.label"]) ?? "?";
    return title;
  }, [dev_session, stack?.name]);

  const tagDisplayText = ISXUtils.getTagDisplayText(
    tag.attribute,
    stacks?.[tag.stack]?.data_config,
    true
  );
  return `${stackTitle}:${tagDisplayText}`;
};

const AnimatedTableCell = (props) => {
  const { children, direction, container, ...rest } = props;
  return (
    <TableCell {...rest}>
      <Slide
        container={container}
        direction={direction}
        in={true}
        timeout={ANIMATION_DURATION}
      >
        {children}
      </Slide>
    </TableCell>
  );
};

const RangeButton = styled(Button)(({theme})  => (  {
  color: theme.palette.text.primary
}));

const PeriodsWidget = (props) => {
  const [direction, setDirection] = useState("right");
  const [animating, setAnimating] = useState(false);
  const animationContainerRef = React.useRef(null);
  const { widget, stacks, periods, data, loading, offset, setOffset } = props;
  const { tags = [], options } = widget;
  const { "periods.tracking": tracking, periods_to_show: periodsToShow = 0 } =
    options ?? {};
  //console.log("periods in rendering",periods,tracking);
  //console.log("render data",tags,data);

  const tableDump = () => {
  
    var table = document.getElementById("table"),rIndex,cIndex;
    // table rows
    for (var i =0;i<table.rows.length;i++)
      {
        // row cells
        console.log("rows are",i,table.rows[i]);          
    }
  }

  const exportToCSV = () => {
    const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

    // columns:
    // - tag
    // - period name
    // - period start in ISO 8601 format
    // - period end in ISO 8601 format
    // - period start in excel-friendly format
    // - period end in excel-friendly format
    // - tracking type ("counts" or "states")
    // - state (if tracking type is "states")
    // - summary unit (% of time, total time, counts; if tracking type is "states"),
    // - value

    const headers = ["Tag"].concat(periods.map((period, idx) => {
      const range =
        period.start.year() !== period.end.year() ||
        period.start.month() !== period.end.month() ||
        period.start.date() !== period.end.date()
          ? `${period.start.format(
              "ddd MMM D HH:mm"
            )} - ${period.end.format("ddd MMM D HH:mm")}`
          : `${period.start.format(
              "ddd MMM D HH:mm"
            )} - ${period.end.format("HH:mm")}`;
      return period.template.period.label ? `${range} ( ${period.template.period.label} )` : range
    }));
    const body = tags.flatMap((tag) => {
      const dev_session = stacks?.[tag.stack]?.dev_session ?? "{}";
      const stackTitle = (stacks?.[tag.stack]?.name || JSON.parse(dev_session)["stack.label"]) ?? "?";
      const tagDisplayText = ISXUtils.getTagDisplayText(
        tag.attribute,
        stacks?.[tag.stack]?.data_config,
        false
      );
      const tagLabel = `${stackTitle}:${tagDisplayText}`;

      const trackingType = tracking[tag.guuid]?.trackingType;
      if (trackingType === "counts") {
        const rowdata = [tagLabel].concat(periods.flatMap((period) => {
          const dataKey = JSON.stringify([
            tag.stack,
            tag.attribute,
            period.startValue,
            period.endValue,
          ]);
          return data[dataKey];
        }));
        return [rowdata];
      }
      else {
        const summaryUnit = tracking[tag.guuid]?.summaryUnit ?? "percent";
        const ddata = {};
        const tdata = {};
        if(tracking?.[tag?.guuid]?.display?.intervals?.enabled) {
          tracking?.[tag?.guuid]?.display?.intervals?.labels.forEach((label) => ddata[label] = []);
        }
        if(tracking?.[tag?.guuid]?.display?.transitions?.enabled) {
        tracking?.[tag?.guuid]?.display?.transitions?.labels.forEach((label) => tdata[label] = []);
        }
        periods.forEach((period) => {
          const dataKey = JSON.stringify([
            tag.stack,
            tag.attribute,
            period.startValue,
            period.endValue,
          ]);
          const value = data[dataKey];
          //const tlabels = Object.keys(value?.['count'] ?? []).filter(x => (tracking?.[tag?.guuid]?.display?.transitions?.labels ?? Object.keys(value?.['count'] ?? [])).includes(x));
          //const dlabels = Object.keys(value?.[summaryUnit] ?? []).filter(x => (tracking?.[tag?.guuid]?.display?.intervals?.labels ?? Object.keys(value?.[summaryUnit] ?? [])).includes(x));
          Object.keys(ddata).forEach((label) => {
            const val =value?.[summaryUnit]?.[label];
            let formattedValue;
            if (summaryUnit === "percent") {
              formattedValue = isNaN(val) ? null : Number(val);
            } else if (summaryUnit === "time") {
              formattedValue = moment
                .utc((isNaN(val) ? 0 : val) * 1000)
                .format("HH:mm:ss");
            } else {
              formattedValue = isNaN(val) ? null : Number(val);
            }
            ddata[label].push(formattedValue);
          });
          Object.keys(tdata).forEach((label) => {
            tdata[label].push(isNaN(value?.['count']?.[label]) ? 0 : Number(value?.['count']?.[label]));
          });
        });
        const retval = Object.keys(ddata).toSorted().map((label) => {
          return [`${stackTitle}:${tagDisplayText}:${label} ${summaryUnit === "percent" ? "%" : "time"}`].concat(ddata[label])
        }).concat(
          Object.keys(tdata).toSorted().map((label) => {
            return [`${stackTitle}:${tagDisplayText}:${label} transitions`].concat(tdata[label])
        }));
        return retval
      }
    });

    const filename = `${(widget.title ?? "Report")
      .replace(/\s+/g, "")
      .replace(/,/g, "-")}.csv`;

    CSVExporter.export(filename, [headers, ...body]);
  };

  const oldexportToCSV = () => {
    const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

    // columns:
    // - tag
    // - period name
    // - period start in ISO 8601 format
    // - period end in ISO 8601 format
    // - period start in excel-friendly format
    // - period end in excel-friendly format
    // - tracking type ("counts" or "states")
    // - state (if tracking type is "states")
    // - summary unit (% of time, total time, counts; if tracking type is "states"),
    // - value
    const headers = [
      "Tag",
      "Period Name",
      "Period Start ISO 8601",
      "Period End ISO 8601",
      "Period Start",
      "Period End",
      "Tracking Type",
      "State",
      "Summary Unit",
      "Value",
    ];

    const body = tags.flatMap((tag) => {
      const dev_session = stacks?.[tag.stack]?.dev_session ?? "{}";
      const stackTitle =
        (stacks?.[tag.stack]?.name || JSON.parse(dev_session)["stack.label"]) ??
        "?";
      const tagDisplayText = ISXUtils.getTagDisplayText(
        tag.attribute,
        stacks?.[tag.stack]?.data_config,
        false
      );
      const tagLabel = `${stackTitle}:${tagDisplayText}`;

      const trackingType = tracking[tag.guuid]?.trackingType;
      return periods.flatMap((period) => {
        const periodLabel = period.template.period.label ?? "";
        const startISO = period.start.format();
        const endISO = period.end.format();
        const start = period.start.format(DATE_TIME_FORMAT);
        const end = period.end.format(DATE_TIME_FORMAT);
        const dataKey = JSON.stringify([
          tag.stack,
          tag.attribute,
          period.startValue,
          period.endValue,
        ]);
        const value = data[dataKey];
        if (trackingType === "counts") {
          return [
            [
              tagLabel,
              periodLabel,
              startISO,
              endISO,
              start,
              end,
              trackingType,
              null,
              null,
              value,
            ],
          ];
        } else {
          // states
          const summaryUnit = tracking[tag.guuid]?.summaryUnit ?? "percent";
          const entries = Object.entries(value?.summaryUnit ?? []);
          return entries.map(([label, val]) => {
            let formattedValue;
            if (summaryUnit === "percent") {
              formattedValue = isNaN(val) ? null : Number(val);
            } else if (summaryUnit === "time") {
              formattedValue = moment
                .utc((isNaN(val) ? 0 : val) * 1000)
                .format("HH:mm:ss");
            } else {
              formattedValue = isNaN(val) ? null : Number(val);
            }
            return [
              tagLabel,
              periodLabel,
              startISO,
              endISO,
              start,
              end,
              trackingType ?? "states",
              label,
              summaryUnit,
              formattedValue,
            ];
          });
        }
      });
    });

    const filename = `${(widget.title ?? "Report")
      .replace(/\s+/g, "")
      .replace(/,/g, "-")}.csv`;

    CSVExporter.export(filename, [headers, ...body]);
  };

  const showPrevious = () => {
    setOffset((offset) => offset + Math.max(periodsToShow - 1, 1));
    setDirection("left");
    setAnimating(true);
    setTimeout(() => setAnimating(false), ANIMATION_DURATION);
  };

  const showNext = () => {
    setOffset((offset) => Math.max(0, offset - Math.max(periodsToShow - 1, 1)));
    setDirection("right");
    setAnimating(true);
    setTimeout(() => setAnimating(false), ANIMATION_DURATION);
  };

  const reset = () => {
    setOffset(0);
    setDirection("right");
    setAnimating(true);
    setTimeout(() => setAnimating(false), ANIMATION_DURATION);
  };

  const getLabelIndicators = (indicators) => {
    let labelIndicators = {}
    indicators.forEach((i) => {
      if(i?.derivedlabel) {
        if(!labelIndicators[i?.derivedlabel]) {
          labelIndicators[i?.derivedlabel] = []
        }
        labelIndicators[i?.derivedlabel].push(i);
    }})
    return labelIndicators;
  }

  const _computeDisplayOptions = (indicators,value) => {
    let displayOptions = {};
    ISXUtils.buildIndicators(indicators).forEach((op) => {
      displayOptions = op(value) || displayOptions;
    });
    return displayOptions;
  };
  
  const reportOptions = () => {
    return {
      tags: [...props.widget?.tags],
      options: {
        start: 176538638746
        
      }
    };
  }

  return (
    <Widget
      {...props}
      configurable={true}
      showDerivedTags={false}
      dataType="tags"
      widgetTitle="Multi-tag Report"
      exportToCSV={exportToCSV}
      reportOptions={reportOptions}
    >
      <div style={{ height: "calc(100% - 36px)", overflow: "auto" }}>
        <Backdrop
          style={{ height: "calc(100% - 20px)", top: 20, zIndex: 100 }}
          open={loading && periods.length > 0}
        >
          <Paper style={{ backgroundColor: "white", padding: 10 }}>
            Loading Data...
          </Paper>
        </Backdrop>
        <Table
          ref={animationContainerRef}
          style={{ overflow: animating ? "hidden" : "auto" }}
          id="table"
        >
          <TableHead>
            <TableRow>
              <StickyColumnTableCell />
              {periods.map((period, idx) => {
                const key = JSON.stringify([
                  period.template.period.guuid,
                  //period.template.day,
                  idx,
                ]);
                const range =
                  period.start.year() !== period.end.year() ||
                  period.start.month() !== period.end.month() ||
                  period.start.date() !== period.end.date()
                    ? `${period.start.format(
                        "ddd MMM D HH:mm"
                      )} - ${period.end.format("ddd MMM D HH:mm")}`
                    : `${period.start.format(
                        "ddd MMM D HH:mm"
                      )} - ${period.end.format("HH:mm")}`;
                return (
                  <AnimatedTableCell
                    key={key}
                    direction={direction}
                    container={animationContainerRef.current}
                    style={{ textAlign: "right" }}
                  >
                    <div>
                      <Typography
                        variant="overline"
                        style={{ lineHeight: "1rem" }}
                      >
                        {period.template.period.label || ""}
                      </Typography>
                      <Typography style={{ fontWeight: 600 }}>
                        {range}
                      </Typography>
                    </div>
                  </AnimatedTableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {tags.map((t, idx) => { 
              const indicatorsByLabel = t?.indicator_source === "custom" ? getLabelIndicators(t?.indicators || []) : {}
              return (
              <TableRow key={t.guuid}>
                <StickyColumnTableCell
                  component="th"
                  scope="row"
                  // className={classes.stickyColumn}
                >
                  <TagLabel tag={t} stacks={stacks} />
                </StickyColumnTableCell>
                {periods.map((period, idx) => {
                  const { stack, attribute } = t;
                  const key = JSON.stringify([
                    period.template.period.guuid,
                    //period.template.day,
                    stack,
                    attribute,
                    idx,
                  ]);
                  const dataKey = JSON.stringify([
                    stack,
                    attribute,
                    period.startValue,
                    period.endValue,
                  ]);
                  
                  const value = data[dataKey];
                  //console.log(t.guuid,dataKey,value);
                  const trackingType = tracking[t.guuid]?.trackingType;
                  if (trackingType === "counts") {
                    const displayOptions = _computeDisplayOptions(indicatorsByLabel["count"] || [],value);
                    return (
                      <AnimatedTableCell
                        key={key}
                        direction={direction}
                        container={animationContainerRef.current}
                        style={{ textAlign: "right" }}
                      >
                        <div style={{ color: displayOptions?.color, backgroundColor:displayOptions?.bgcolor,}}>
                          <span style={displayOptions?.color ? {fontWeight:'bold'} : {fontWeight:'normal'} }>Counts:&nbsp;
                          {!isNaN(value) ? (
                            <NumberFormat
                              value={value}
                              displayType="text"
                              thousandSeparator={true}
                              allowNegative={true}
                              decimalScale={2}
                            />
                          ) : (
                            <div>-</div>
                          )}
                        </span></div>
                      </AnimatedTableCell>
                    );
                  } else {
                    const summaryUnit = tracking[t.guuid]?.summaryUnit ?? "percent";
                    //const entries = Object.entries(value?.[summaryUnit] ?? []);
                    const dlabels = (tracking?.[t?.guuid]?.display?.intervals?.enabled ?? true ) ? Object.keys(value?.[summaryUnit] ?? []).filter(x => (tracking?.[t?.guuid]?.display?.intervals?.labels ?? Object.keys(value?.[summaryUnit] ?? [])).includes(x)) : [];
                    const tlabels = (tracking?.[t?.guuid]?.display?.transitions?.enabled ?? true) ? Object.keys(value?.count ?? []).filter(x => (tracking?.[t?.guuid]?.display?.transitions?.labels ?? Object.keys(value?.transitions ?? [])).includes(x)): []
                    //console.log("summaryUnit and labels",summaryUnit,dlabels,Object.keys(value?.[summaryUnit] ?? []),tracking?.[t?.guuid]?.display?.intervals?.labels);
                    return (
                      <AnimatedTableCell
                        key={key}
                        container={animationContainerRef.current}
                        direction={direction}
                        style={{ textAlign: "right" }}
                      >
                        {(dlabels.length > 0 || tlabels.length > 0) ? (
                          <div><div>
                            {dlabels.map((label) => {
                              const val = value?.[summaryUnit]?.[label];
                              const displayOptions = _computeDisplayOptions(indicatorsByLabel[label] || [], val);
                              let formattedValue;
                              if (summaryUnit === "percent") {
                                const numval = Number(val);
                                formattedValue = `${numval === 100 || numval === 0
                                    ? numval
                                    : isNaN(val)
                                      ? "___"
                                      : val}%`;
                              } else if (summaryUnit === "time") {
                                const dd = Math.floor((isNaN(val) ? 0 : val) / 86400);
                                if (dd === 1) {
                                  formattedValue = "1 day " + moment
                                    .utc((isNaN(val) ? 0 : val % 86400) * 1000)
                                    .format("HH:mm:ss");
                                }
                                else if (dd > 1) {
                                  formattedValue = `${dd.toString()} days ${moment
                                    .utc((isNaN(val) ? 0 : val % 86400) * 1000)
                                    .format("HH:mm:ss")}`;
                                }
                                else {
                                  formattedValue = moment
                                    .utc((isNaN(val) ? 0 : val) * 1000)
                                    .format("HH:mm:ss");
                                }
                              } else {
                                formattedValue = isNaN(val) ? "---" : val;
                              }
                              return (
                                <div key={label} style={{ color: displayOptions?.color, backgroundColor: displayOptions?.bgcolor, }}>
                                  <span style={displayOptions?.color ? { fontWeight: 'bold' } : { fontWeight: 'normal' }}>{label}:&nbsp;{formattedValue}</span>
                                </div>
                              );
                            })}
                          </div>
                          <div>
                          {
                            tlabels.map((label) => {
                              const val = value?.count?.[label];
                              const displayOptions = _computeDisplayOptions(indicatorsByLabel[label] || [], val);
                              return (
                                <div key={label} style={{ color: displayOptions?.color, backgroundColor: displayOptions?.bgcolor, }}>
                                  <span style={displayOptions?.color ? { fontWeight: 'bold' } : { fontWeight: 'normal' }}>{label+" Transitions "}:&nbsp;{isNaN(val) ? "---" : val}</span>
                                </div>
                              );
                            })
                          }
                          </div>
                          </div>
                        ) : (
                          <div>-</div>
                        )}
                      </AnimatedTableCell>
                    );
                  }
                })}
              </TableRow>
            )})}
          </TableBody>
        </Table>
      </div>
      <Grid container style={{ height: 36 }} justifyContent="space-between">
        <Grid item>
          <RangeButton onClick={showPrevious} disabled={loading}>
            <NavigateBeforeIcon />
            Previous
          </RangeButton>
        </Grid>
        <Grid item>
          <RangeButton onClick={reset} disabled={loading || offset === 0}>
            Reset
          </RangeButton>
        </Grid>
        <Grid item>
          <RangeButton onClick={showNext} disabled={loading || offset === 0}>
            Next <NavigateNextIcon />
          </RangeButton>
        </Grid>
      </Grid>
    </Widget>
  );
};

export default PeriodsWidget;
